<template>
  <div class="my-6 text-start">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="font-bold">Detalhes do Chamado #{{ chamado.id || route.params.id }}</h3>
        <span class="text-sm text-gray-500">Visualize e gerencie os detalhes do chamado de suporte.</span>
      </div>
      <div class="flex gap-3">
        <Button 
          v-if="chamado.status === 'aberto'" 
          color="primary" 
          :text="`Encerrar Chamado`" 
          @click="confirmarEncerramento" 
        />
      </div>
    </div>
  </div>

  <!-- Layout de coluna única centralizada -->
  <div class="max-w-4xl mx-auto space-y-6">
    <!-- Loading state -->
    <div v-if="!chamado.id" class="flex justify-center items-center py-16">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p class="text-gray-500">Carregando detalhes do chamado...</p>
      </div>
    </div>

    <!-- Conteúdo quando carregado -->
    <div v-else class="space-y-6">
      <!-- Informações Principais -->
      <div class="card shadow-sm border p-8 bg-white">
      <div class="w-full">
        <div class="flex-3 w-full mb-7">
          <h3 class="font-bold">Informações do Chamado</h3>
          <span class="text-sm text-gray-500">
            Dados principais do chamado de suporte.
          </span>
        </div>
      </div>

      <div class="flex lg:flex-row flex-col mt-5">
        <div class="w-full p-2">
          <div class="mb-2">
            <label class="block text-sm font-medium text-gray-700 mb-2">Assunto</label>
            <p class="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg border">{{ chamado.assunto || '-' }}</p>
          </div>
        </div>
        <div class="w-full p-2">
          <div class="mb-2">
            <label class="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <span :class="getStatusClass(chamado.status)" class="inline-flex px-2 py-1 text-xs font-medium rounded-full">
              {{ getStatusLabel(chamado.status) }}
            </span>
          </div>
        </div>
      </div>

      <div class="flex lg:flex-row flex-col mt-5">
        <div class="w-full p-2">
          <div class="mb-2">
            <label class="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
            <span :class="getTipoClass(chamado.tipo)" class="inline-flex px-2 py-1 text-xs font-medium rounded-full">
              {{ getTipoLabel(chamado.tipo) }}
            </span>
          </div>
        </div>
        <div class="w-full p-2">
          <div class="mb-2">
            <label class="block text-sm font-medium text-gray-700 mb-2">Criado em</label>
            <p class="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg border">{{ dataFormatada.criacao || '-' }}</p>
          </div>
        </div>
      </div>

      <div class="flex lg:flex-row flex-col mt-5">
        <div class="w-full p-2">
          <div class="mb-2">
            <label class="block text-sm font-medium text-gray-700 mb-2">Evento</label>
            <p class="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg border">
              {{ getEventoDetalhado() }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex lg:flex-row flex-col mt-5">
        <div class="w-full p-2">
          <div class="mb-2">
            <label class="block text-sm font-medium text-gray-700 mb-2">Usuário</label>
            <p class="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg border">{{ chamado.usuario?.usuario || '-' }}</p>
          </div>
        </div>
        <div class="w-full p-2">
          <div class="mb-2">
            <label class="block text-sm font-medium text-gray-700 mb-2">ID do Usuário</label>
            <p class="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg border">#{{ chamado.usuario?.id || '-' }}</p>
          </div>
        </div>
      </div>

      <div class="flex lg:flex-row flex-col mt-5">
        <div class="w-full p-2">
          <div class="mb-2">
            <label class="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
            <div class="bg-gray-50 p-4 rounded-lg border min-h-[100px]">
              <p class="text-sm text-gray-900 whitespace-pre-wrap">{{ chamado.descricao || '-' }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Timeline/Histórico -->
    <div class="card shadow-sm border p-8 bg-white">
      <div class="w-full">
        <div class="flex-3 w-full mb-7">
          <h3 class="font-bold">Histórico do Chamado</h3>
          <span class="text-sm text-gray-500">
            Acompanhe todas as atividades relacionadas a este chamado.
          </span>
        </div>
      </div>

      <div class="space-y-4">
        <div v-if="timeline.length === 0" class="text-center py-8 text-gray-500">
          <p>Nenhuma atividade registrada ainda.</p>
        </div>
        
        <div v-for="(item, index) in timeline" :key="item.id" class="flex">
          <div class="flex-shrink-0">
            <div class="flex items-center justify-center w-8 h-8 bg-blue-500 rounded-full">
              <fa :icon="getTimelineIcon(item.tipoAcao)" class="w-4 h-4 text-white" />
            </div>
            <div v-if="index < timeline.length - 1" class="w-px bg-gray-300 ml-4 mt-2" style="height: 2rem;"></div>
          </div>
          <div class="ml-4 pb-4 flex-1">
            <div class="flex items-center justify-between">
              <h4 class="text-sm font-medium text-gray-900">{{ getTimelineTitle(item.tipoAcao) }}</h4>
              <span class="text-xs text-gray-500">{{ formatarData(item.createdAt) }}</span>
            </div>
            <p class="text-sm text-gray-600 mt-1">Por: {{ item.criadoPor?.usuario || 'Sistema' }}</p>
            <div v-if="item.mensagem" class="mt-2 p-3 bg-gray-50 rounded-md">
              <p class="text-sm text-gray-700">{{ item.mensagem }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Adicionar Comentário - Agora embaixo da timeline -->
      <div v-if="chamado.status === 'aberto'" class="mt-8 pt-6 border-t border-gray-200">
        <h3 class="font-bold mb-4">Adicionar Comentário</h3>
        
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Comentário</label>
            <textarea 
              v-model="novoComentario.texto" 
              rows="4" 
              placeholder="Digite seu comentário..."
              class="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            ></textarea>
          </div>
          
          <div class="flex justify-end">
            <Button 
              color="primary" 
              :text="`Adicionar Comentário`" 
              @click="adicionarComentario"
              :disabled="!novoComentario.texto.trim()"
            />
          </div>
        </div>
      </div>

      <!-- Aviso para chamados encerrados -->
      <div v-if="chamado.status === 'encerrado'" class="mt-8 pt-6 border-t border-gray-200">
        <div class="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div class="flex">
            <fa icon="exclamation-triangle" class="w-5 h-5 text-yellow-400" />
            <div class="ml-3">
              <h3 class="text-sm font-medium text-yellow-800">Chamado Encerrado</h3>
              <div class="mt-2 text-sm text-yellow-700">
                <p>Este chamado foi encerrado e não pode ser reaberto. Se necessário, solicite ao cliente que abra um novo chamado.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div> <!-- Fecha o v-else -->
  </div>
</template>

<script>
import Button from "../../components/Elements/Button.vue";
import { GET, PUT, POST } from "../../services/api";
import { inject, onMounted, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { 
  formatarDataTimezone, 
  formatarDataSomente, 
  tempoDecorrido as calcularTempoDecorrido 
} from "../../services/timezone";

export default {
  name: 'SuporteDetails',
  components: {
    Button,
  },

  setup() {
    const route = useRoute();
    const router = useRouter();
    const alert = inject("alert");
    const loader = inject("loading"); // Corrigido para 'loading'
    const modal = inject("modal");

    const chamado = ref({});
    const timeline = ref([]);
    const novoComentario = ref({
      texto: ''
    });

    // Funções utilitárias (declaradas primeiro)
    const formatarData = (dataString) => {
      if (!dataString) return '-';
      return formatarDataTimezone(dataString);
    };

    const getStatusClass = (status) => {
      const statusConfig = {
        'aberto': 'bg-blue-100 text-blue-800',
        'encerrado': 'bg-gray-100 text-gray-800'
      };
      return statusConfig[status] || 'bg-gray-100 text-gray-800';
    };

    const getStatusLabel = (status) => {
      const statusConfig = {
        'aberto': 'Aberto',
        'encerrado': 'Encerrado'
      };
      return statusConfig[status] || status;
    };

    const getTipoClass = (tipo) => {
      const tipoConfig = {
        'criar_ingresso': 'bg-green-100 text-green-800',
        'alterar_lote': 'bg-orange-100 text-orange-800',
        'outros': 'bg-blue-100 text-blue-800'
      };
      return tipoConfig[tipo] || 'bg-gray-100 text-gray-800';
    };

    const getTipoLabel = (tipo) => {
      const tipoConfig = {
        'criar_ingresso': 'Criar Ingresso',
        'alterar_lote': 'Alterar Lote',
        'outros': 'Outros'
      };
      return tipoConfig[tipo] || tipo;
    };

    const getTimelineIcon = (tipoAcao) => {
      const iconConfig = {
        'abertura': 'plus',
        'comentario': 'comment',
        'status_change': 'arrow-right',
        'encerramento': 'check'
      };
      return iconConfig[tipoAcao] || 'circle';
    };

    const getTimelineTitle = (tipoAcao) => {
      const titleConfig = {
        'abertura': 'Chamado Criado',
        'comentario': 'Comentário Adicionado',
        'status_change': 'Status Alterado',
        'encerramento': 'Chamado Encerrado'
      };
      return titleConfig[tipoAcao] || 'Atividade';
    };

    const getEventoDetalhado = () => {
      if (!chamado.value.evento) return '-';
      
      const evento = chamado.value.evento;
      const id = evento.id || '-';
      const nome = evento.nome || '-';
      
      // Formatar data do evento
      let dataEvento = '-';
      if (evento.data_inicio) {
        dataEvento = formatarDataSomente(evento.data_inicio);
      }
      
      return `#${id} - ${nome} - ${dataEvento}`;
    };

    // Função de encerramento (declarada antes de ser usada)
    const encerrarChamado = async () => {
      try {
        if (loader && loader.open) {
          loader.open();
        }
        
        await PUT(`/admin/chamados/${route.params.id}/status`, { status: 'encerrado' });
        
        // Atualizar status local
        chamado.value.status = 'encerrado';
        chamado.value.closedAt = new Date().toISOString();
        chamado.value.updatedAt = new Date().toISOString();
        
        // Adicionar evento à timeline
        timeline.value.push({
          id: Date.now(), // ID temporário
          chamadoId: chamado.value.id,
          tipoAcao: 'encerramento',
          mensagem: 'Chamado encerrado pelo administrador',
          createdAt: new Date().toISOString(),
          createdBy: 'admin', // ID do admin
          criadoPor: { usuario: 'Admin' }
        });
        
        if (loader && loader.close) {
          loader.close();
        }
        
        if (alert && alert.open) {
          alert.open("Sucesso!", "Chamado encerrado com sucesso", "success");
        }
        
      } catch (error) {
        if (loader && loader.close) {
          loader.close();
        }
        console.error('Erro ao encerrar chamado:', error);
        if (alert && alert.open) {
          alert.open("Erro", "Não foi possível encerrar o chamado", "danger");
        }
      }
    };

    // Computed para dados formatados
    const dataFormatada = computed(() => ({
      criacao: formatarData(chamado.value.createdAt),
      atualizacao: formatarData(chamado.value.updatedAt)
    }));

    const tempoDecorrido = computed(() => {
      if (!chamado.value.createdAt) return '';
      return calcularTempoDecorrido(chamado.value.createdAt);
    });

    // Funções de carregamento e ação
    const carregarDetalhes = async () => {
      try {
        console.log('Carregando detalhes para chamado ID:', route.params.id);
        
        // Verificar se loader está disponível
        if (loader && loader.open) {
          loader.open();
        }
        
        const response = await GET(`/admin/chamados/${route.params.id}`);
        console.log('Resposta da API:', response);
        
        // A API retorna { success: true, data: chamado }
        if (response && response.data) {
          const chamadoData = response.data;
          
          // Extrair timeline e remover do objeto principal
          const { timeline: timelineData, ...chamadoLimpo } = chamadoData;
          
          chamado.value = chamadoLimpo;
          timeline.value = timelineData || [];
        } else {
          throw new Error('Dados não encontrados na resposta da API');
        }
        
        console.log('Chamado carregado:', chamado.value);
        console.log('Timeline carregada:', timeline.value);
        
        // Verificar se loader está disponível antes de fechar
        if (loader && loader.close) {
          loader.close();
        }
      } catch (error) {
        // Verificar se loader está disponível antes de fechar
        if (loader && loader.close) {
          loader.close();
        }
        console.error('Erro ao carregar detalhes:', error);
        
        // Verificar se alert está disponível
        if (alert && alert.open) {
          alert.open("Erro", `Não foi possível carregar os detalhes do chamado: ${error.message}`, "danger");
        }
        // Comentando o redirecionamento para debug
        // router.push('/suporte');
      }
    };

    const adicionarComentario = async () => {
      if (!novoComentario.value.texto.trim()) return;

      try {
        if (loader && loader.open) {
          loader.open();
        }
        
        await POST(`/admin/chamados/${route.params.id}/comentarios`, {
          mensagem: novoComentario.value.texto
        });

        // Adicionar comentário à timeline local
        timeline.value.push({
          id: Date.now(), // ID temporário
          chamadoId: chamado.value.id,
          tipoAcao: 'comentario',
          mensagem: novoComentario.value.texto,
          createdAt: new Date().toISOString(),
          createdBy: 'admin', // ID do admin
          criadoPor: { usuario: 'Admin' }
        });

        // Limpar formulário
        novoComentario.value.texto = '';
        
        if (loader && loader.close) {
          loader.close();
        }
        
        if (alert && alert.open) {
          alert.open("Sucesso!", "Comentário adicionado com sucesso", "success");
        }
        
      } catch (error) {
        if (loader && loader.close) {
          loader.close();
        }
        console.error('Erro ao adicionar comentário:', error);
        if (alert && alert.open) {
          alert.open("Erro", "Não foi possível adicionar o comentário", "danger");
        }
      }
    };

    const confirmarEncerramento = () => {
      if (modal && modal.open) {
        modal.open(
          "Confirmar Encerramento",
          "Deseja realmente encerrar este chamado? Esta ação não pode ser desfeita.",
          "warning",
          "Sim, Encerrar",
          () => encerrarChamado(),
          modal.close
        );
      }
    };

    // Inicialização
    onMounted(async () => {
      await carregarDetalhes();
    });

    return {
      route,
      router,
      chamado,
      timeline,
      novoComentario,
      dataFormatada,
      tempoDecorrido,
      formatarData,
      getStatusClass,
      getStatusLabel,
      getTipoClass,
      getTipoLabel,
      getTimelineIcon,
      getTimelineTitle,
      getEventoDetalhado,
      carregarDetalhes,
      adicionarComentario,
      confirmarEncerramento,
      encerrarChamado,
    };
  }
}
</script>

<style scoped>
.card {
  border-radius: 8px;
}
</style>